import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import {
  FiCalendar,
  FiDollarSign,
  FiDownload,
  FiFileText,
  FiLock,
  FiMapPin,
  FiMessageCircle,
  FiShield,
  FiThermometer,
  FiUser,
} from 'react-icons/fi';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import { AtAGlance, Footer, MarketingHeader, SEO } from '@/components';
import { track } from '@/lib/analytics';
import { buildHarborSoftwareApplicationJsonLd, HARBOR_FACTS } from '@/lib/harbor-facts';
import { getAppStoreUrl, getPlayStoreUrl, getUtmParamsFromUrl } from '@/lib/urls';

const FEATURES = [
  {
    icon: FiMessageCircle,
    slug: 'secure-messaging',
    title: 'Secure co-parent messaging',
    description:
      'Keep every conversation documented in one place. Harbor provides a co-parenting platform with secure messaging so high-conflict exchanges stay on record and out of personal texts.',
  },
  {
    icon: FiDollarSign,
    slug: 'expense-tracking',
    title: 'Expense tracking & split calculations',
    description:
      'Log child-related expenses, split costs automatically, and maintain a clear ledger. Harbor is a co-parenting expense app built for shared financial accountability.',
  },
  {
    icon: FiCalendar,
    slug: 'custody-calendar',
    title: 'Custody calendar & time logs',
    description:
      'Manage custody schedules, exceptions, and parenting time in one shared calendar. Harbor works as a custody tracking app with expense and time logs in a single workflow.',
  },
  {
    icon: FiFileText,
    slug: 'receipt-scanner',
    title: 'Receipt scanner with smart tagging',
    description:
      'Snap a photo of any receipt and Harbor extracts the amount, merchant, and date. Attach receipts to expense records for a complete audit trail.',
  },
  {
    icon: FiDownload,
    slug: 'court-ready-exports',
    title: 'One-tap court-ready exports',
    description:
      'Export messages, expenses, and calendar records in seconds. Generate comprehensive reports for attorneys or court filings without manual assembly.',
  },
  {
    icon: FiShield,
    slug: 'court-ready-exports',
    title: 'Court-ready records',
    description:
      'Harbor maintains tamper-evident records with timestamps and audit trails designed for family-law workflows across the US and Canada.',
  },
  {
    icon: FiLock,
    slug: 'private-journal',
    title: 'Private journal',
    description:
      'A personal notebook for medical visits, handoffs, and notes, visible only to you, not shared with your co-parent.',
  },
  {
    icon: FiThermometer,
    slug: 'temperature-check',
    title: 'Temperature Check',
    description:
      'Score a draft message in conversation context and get a calmer, BIFF-style rewrite before you send. Harbor’s equivalent of incumbent tone tools such as OurFamilyWizard ToneMeter, included in the paid plan.',
  },
  {
    icon: FiMapPin,
    slug: 'gps-check-ins',
    title: 'GPS exchange check-ins',
    description:
      'Location-verified custody handoffs with on-time, late, and no-show tracking. Export the compliance record with your other court-ready reports.',
  },
  {
    icon: FiUser,
    title: 'Works with one parent',
    description:
      'Start Harbor solo. Log expenses, keep a custody calendar, and build a record before the other parent accepts an invite. You do not need both parents onboarded on day one.',
  },
];

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://harborcoparent.com/' },
    { '@type': 'ListItem', position: 2, name: 'Features', item: 'https://harborcoparent.com/features' },
  ],
};

const itemListJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Harbor Co-Parenting Features',
  itemListElement: FEATURES.map((feature, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: feature.title,
    description: feature.description,
  })),
};

function FeaturesPage() {
  useEffect(() => {
    track('Features Screen');
  }, []);

  const utm = getUtmParamsFromUrl();

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Co-Parenting App Features: Messaging, Expenses & Custody | Harbor'
        description='Harbor is $9.99/month or $49.99/year per person with secure messaging, Temperature Check, expenses, custody calendars, GPS check-ins, and court-ready exports. iOS, Android, and web.'
        path='/features'
        jsonLd={[breadcrumbJsonLd, itemListJsonLd, buildHarborSoftwareApplicationJsonLd()]}
      />
      <MarketingHeader />

      <section className='bg-primary/5 pt-32 pb-12'>
        <div className='mx-auto max-w-4xl px-6 text-center'>
          <h1 className='mb-4 text-4xl font-bold text-gray-900 md:text-5xl'>
            Co-parenting platform features
          </h1>
          <p className='mx-auto max-w-2xl text-xl leading-relaxed text-gray-600'>
            {HARBOR_FACTS.quote}
          </p>
        </div>
      </section>

      <AtAGlance />

      <section className='mx-auto max-w-5xl px-6 py-16'>
        <div className='space-y-12'>
          {FEATURES.map((feature) => {
            const Icon = feature.icon;
            return (
              <article key={feature.title} className='flex gap-6'>
                <div className='flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary/10'>
                  <Icon className='h-7 w-7 text-primary' />
                </div>
                <div>
                  <h2 className='text-2xl font-bold text-gray-900'>{feature.title}</h2>
                  <p className='mt-2 text-lg leading-relaxed text-gray-600'>{feature.description}</p>
                  {feature.slug && (
                    <Link
                      to='/blog/$slug'
                      params={{ slug: feature.slug }}
                      className='mt-3 inline-block font-semibold text-primary hover:underline'
                      onClick={() =>
                        track('Click Blog Post', { slug: feature.slug, location: 'Features' })
                      }
                    >
                      Read more
                    </Link>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className='mx-auto max-w-5xl px-6 pb-4'>
        <div className='rounded-2xl border border-gray-200 bg-sky-light p-6'>
          <h2 className='text-xl font-bold text-gray-900'>Compare what these features cost</h2>
          <p className='mt-2 text-gray-700'>
            The{' '}
            <Link to='/coparenting-app-cost-calculator' className='font-semibold text-primary underline'>
              co-parenting app cost calculator
            </Link>{' '}
            estimates one- to five-year costs for Harbor and five competitors, including which
            plans cover messaging, expenses, the calendar, and exports.
          </p>
        </div>
      </section>

      <section className='bg-primary py-14'>
        <div className='mx-auto max-w-4xl px-6 text-center'>
          <h2 className='text-3xl font-bold text-white'>Try Harbor free</h2>
          <p className='mx-auto mt-4 max-w-2xl text-lg text-white/85'>
            Download Harbor on iOS or Android, or sign up on web.
          </p>
          <div className='mt-8 flex flex-col justify-center gap-3 sm:flex-row'>
            <a
              href={getAppStoreUrl('features-page', utm)}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => track('Click App Store', { location: 'Features' })}
              className='inline-flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 font-semibold text-primary hover:bg-slate-100'
            >
              <FaApple size={18} />
              App Store
            </a>
            <a
              href={getPlayStoreUrl('features-page', utm)}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => track('Click Google Play', { location: 'Features' })}
              className='inline-flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 font-semibold text-primary hover:bg-slate-100'
            >
              <FaGooglePlay size={18} />
              Google Play
            </a>
            <Link
              to='/auth/sign-up'
              onClick={() => track('Click Sign Up', { location: 'Features' })}
              className='inline-flex items-center justify-center rounded-lg border border-white px-5 py-3 font-semibold text-white hover:bg-white/10'
            >
              Sign up on web
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/features')({
  component: FeaturesPage,
});
